import AddIcon from '@mui/icons-material/Add';
import { Button, ButtonGroup, FormControlLabel, MenuItem, Switch, TextField } from '@mui/material';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import { ROLE_SUPER_ADMIN, SuperAdminRoles, TenantRoles } from 'shared/permissions/roles';
import TenantPage from 'layouts/TenantPage';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/store';
import toast from 'shared/utils/toast';
import { useUserColumns } from '../hooks/useUserColumns';
import { IUser } from '../types';
import { supplierService } from 'features/inventory/suppliers/api/SupplierService';
import { userService } from '../api/user.service';
import { warehouseService } from 'features/inventory/warehouses/api/warehouse.service';
import CollapsableColumns from 'components/ui/CollapsableColumns';

const initialUser = {
  id: null,
  email: '',
  username: null,
  first_name: null,
  last_name: null,
  groups: [],
  is_ava_enabled: false
};

const disableUser = (user: IUser, miniava_active?: boolean) => {
  user.soft_delete = true;
  user.is_active = false;
  if (miniava_active == true) {
    user.is_ava_enabled = false;
  }
  userService.update(user.id, user).then(() => {
    toast.success('Utente disattivato con successo');
  });
};

export const UserListView = () => {
  const { t } = useTranslation();

  const isMiniActive = JSON.parse(localStorage.getItem('MINIAVA_ENABLED') || 'false');
  const groups = useAppSelector((state) => state.auth.groups);
  const [userToAdd, setUserToAdd] = useState<IUser>(initialUser);
  const [insertUser, setInsertUser] = useState<boolean>(false);
  const [warehouseCodes, setWarehouseCodes] = useState<string[]>([]);
  const [supplierCodes, setSupplierCodes] = useState<string[]>([]);
  const datagridRefresh = useRef(null);
  const isSuperAdmin = groups?.includes(ROLE_SUPER_ADMIN);

  const columns = useUserColumns(warehouseCodes, supplierCodes, disableUser, isMiniActive);
  const translationPrefix = 'pages.users.list';

  useEffect(() => {
    warehouseService.getAllBaseInformation().then((res) => {
      setWarehouseCodes(res.map((wh) => wh.code));
    });
    supplierService.getAllBaseInformation().then((res) => {
      setSupplierCodes(res.map((s) => s.company_name));
    });
  }, []);

  const saveUser = () => {
    userService.add(userToAdd).then((u) => {
      if (u) {
        setUserToAdd(initialUser);
        setInsertUser(false);
        toast.success('Utente aggiunto con successo');
        datagridRefresh.current();
      }
    });
  };

  const abortInsert = () => {
    setUserToAdd(initialUser);
    setInsertUser(false);
  };

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`)}
      subtitle={t(`${translationPrefix}.subtitle`)}
      menuRight={
        <ButtonGroup className="shadow-sm hover:shadow-md transition-shadow duration-200">
          <Button
            startIcon={<AddIcon />}
            sx={{ textTransform: 'none', fontWeight: 'bold' }}
            onClick={() => setInsertUser(!insertUser)}>
            Nuovo Utente
          </Button>
        </ButtonGroup>
      }>
      <CollapsableColumns
        size="w-1/3"
        expand={insertUser}
        onClose={() => setInsertUser(false)}
        contentLeft={
          <EnhancedDataGrid
            refresh={datagridRefresh}
            columns={columns}
            service={userService}
            outerParams={isSuperAdmin ? undefined : { is_active: true }}
            disableDelete
          />
        }
        contentRight={
          <form
            className="flex flex-wrap"
            onSubmit={(e) => {
              e.preventDefault();
              saveUser();
            }}>
            <div className="flex flex-wrap my-4">
              <div className="w-full md:w-1/2 my-2 md:pr-1 px-2">
                <TextField
                  required
                  sx={{ width: '100%' }}
                  label="Username"
                  value={userToAdd.username}
                  onChange={(e) => setUserToAdd({ ...userToAdd, username: e.target.value })}
                />
              </div>
              <div className="w-full md:w-1/2 my-2 md:pl-1 px-2">
                <TextField
                  sx={{ width: '100%' }}
                  select
                  label="Role"
                  value={userToAdd.groups[0]}
                  onChange={(e) => setUserToAdd({ ...userToAdd, groups: [e.target.value] })}>
                  {TenantRoles.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
              <div className="w-full my-2 px-2">
                <TextField
                  sx={{ width: '100%' }}
                  label="Email"
                  type="email"
                  value={userToAdd.email ?? ''}
                  onChange={(e) => setUserToAdd({ ...userToAdd, email: e.target.value })}
                />
              </div>
              <div className="w-full md:w-1/2 my-2  md:pr-1 px-2">
                <TextField
                  required
                  sx={{ width: '100%' }}
                  label="Nome"
                  value={userToAdd.first_name}
                  onChange={(e) => setUserToAdd({ ...userToAdd, first_name: e.target.value })}
                />
              </div>
              <div className="w-full md:w-1/2 my-2  md:pl-1 px-2">
                <TextField
                  required
                  sx={{ width: '100%' }}
                  label="Cognome"
                  value={userToAdd.last_name}
                  onChange={(e) => setUserToAdd({ ...userToAdd, last_name: e.target.value })}
                />
              </div>
              <div className="w-full my-2 px-2">
                <TextField
                  required
                  sx={{ width: '100%' }}
                  label="Password"
                  type="password"
                  value={userToAdd.password}
                  onChange={(e) => setUserToAdd({ ...userToAdd, password: e.target.value })}
                />
              </div>
              <div className="w-full my-2 px-2">
                <TextField
                  required
                  sx={{ width: '100%' }}
                  label="Password confirmation"
                  type="password"
                  value={userToAdd.confirm_password}
                  onChange={(e) => setUserToAdd({ ...userToAdd, confirm_password: e.target.value })}
                />
              </div>
              {SuperAdminRoles.some((role) => groups.includes(role)) && isMiniActive && (
                <div className="w-full my-2 px-2">
                  <FormControlLabel
                    className="w-full my-2 px-2"
                    control={
                      <Switch
                        checked={userToAdd.is_ava_enabled}
                        onChange={(e) =>
                          setUserToAdd({ ...userToAdd, is_ava_enabled: e.target.checked })
                        }
                      />
                    }
                    label={t('global.ava-integration')}
                  />
                </div>
              )}
              <div className="w-full my-2">
                <ButtonGroup className="w-full px-2">
                  <Button
                    onClick={abortInsert}
                    color="warning"
                    variant="outlined"
                    sx={{ width: '100%' }}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="outlined" sx={{ width: '100%' }}>
                    Submit
                  </Button>
                </ButtonGroup>
              </div>
            </div>
          </form>
        }
      />
    </TenantPage>
  );
};

export default UserListView;
